export const SITE_URL = 'https://www.canxp.ai';

export type FaqItem = {
  question: string;
  answer: string;
};

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'CanXP AI',
  url: SITE_URL,
  description:
    'CanXP AI is a Canadian sovereign AI platform for private AI systems, small language model training, secure knowledge bases, and enterprise AI deployment.',
  sameAs: ['https://www.linkedin.com/company/canxp-ai'],
};

export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CanXP AI Platform',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description:
    'A Canadian AI platform for private AI assistants, secure knowledge bases, model orchestration, and sovereign AI workflows.',
};

export const modelTrainingServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'AI Model Training Services',
  provider: {
    '@type': 'Organization',
    name: 'CanXP AI',
  },
  serviceType:
    'Small language model training, fine-tuning, pre-training, quantization, and hosted inference',
  areaServed: 'Canada',
};

export function toSlug(value: string) {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

export function absoluteUrl(path = '/') {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export function socialImageUrl(imageUrl?: string | null) {
  if (!imageUrl) return undefined;

  const absoluteImageUrl = absoluteUrl(imageUrl);
  const storageMatch = absoluteImageUrl.match(/^(https:\/\/service\.canxp\.ai)\/storage\/v1\/object\/public\/(.+)$/);

  if (!storageMatch) return absoluteImageUrl;

  return `${storageMatch[1]}/storage/v1/render/image/public/${storageMatch[2]}?width=1200&height=630&resize=contain&quality=90`;
}

export function buildFaqSchema(faqs: FaqItem[]) {
  return {
    '@context': 'https://schema.org',

    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export function stripHtml(html: string) {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function truncateText(text: string, maxLength = 160) {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
}

export function buildNewsArticleSchema(params: {
  title: string;
  description: string;
  imageUrl?: string | null;
  publishedAt: string;
  authorName: string;
  path: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: params.title,
    description: params.description,
    datePublished: params.publishedAt,
    dateModified: params.publishedAt,
    author: {
      '@type': 'Person',
      name: params.authorName,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CanXP AI',
      url: SITE_URL,
    },
    mainEntityOfPage: absoluteUrl(params.path),
    url: absoluteUrl(params.path),
    image: params.imageUrl ? [socialImageUrl(params.imageUrl)] : undefined,
  };
}

export function buildArticleSchema(params: {

  title: string;
  description: string;
  path: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: params.title,
    description: params.description,
    author: {
      '@type': 'Organization',
      name: 'CanXP AI',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CanXP AI',
      url: SITE_URL,
    },
    mainEntityOfPage: absoluteUrl(params.path),
    url: absoluteUrl(params.path),
  };
}
